import { CountyOption, StateMetadata } from 'interfaces/StateMetadata';
import { SearchResult } from 'interfaces/SearchTypes';
import { stateNameForAbbreviation } from 'utils/stateAbbreviations';

// A state's metadata plus the key it is stored under in the metadata map.
export interface StateSearchItem extends StateMetadata {
  stateName: string;
}

// Finds the states and counties matching a query. Both the metadata key
// ('new_york') and the canonical name ('New York') are full state names, so a
// query like 'NY' is only matched by way of the abbreviation lookup. Counties
// are matched on the abbreviation too, otherwise a county-only state would
// match as a state card that CountyResults then hides.
export const searchStatesAndCounties = (
  query: string,
  statesList: StateSearchItem[],
  counties: CountyOption[],
): SearchResult[] => {
  if (!query.trim()) {
    return [];
  }

  const lowerQuery = query.toLowerCase();
  const abbrevStateName = stateNameForAbbreviation(query);
  const results: SearchResult[] = [];

  // Search counties
  counties.forEach((county) => {
    const countyStateLower = county.county_state.toLowerCase();
    const isInAbbrevState =
      abbrevStateName !== undefined &&
      county.county_state.endsWith(`, ${abbrevStateName}`);

    if (countyStateLower.includes(lowerQuery) || isInAbbrevState) {
      results.push({
        type: 'county',
        county,
        stateName: county.county_state.split(', ')[1], // Extract state name
      });
    }
  });

  // Search states
  statesList.forEach((state) => {
    const metadataKey = state.stateName.toLowerCase();
    const canonicalName = state.state.toLowerCase();

    if (
      metadataKey.includes(lowerQuery) ||
      canonicalName.includes(lowerQuery) ||
      state.state === abbrevStateName
    ) {
      results.push({
        type: 'state',
        state,
        stateName: state.stateName,
      });
    }
  });

  const textFor = (result: SearchResult): string =>
    result.type === 'county'
      ? result.county!.county_state
      : result.state!.stateName;

  // An abbreviation query never prefixes the state's name, so match it here to
  // keep the state people asked for at the top of the results.
  const isTopMatch = (result: SearchResult): boolean =>
    (result.type === 'state' &&
      abbrevStateName !== undefined &&
      result.state!.state === abbrevStateName) ||
    textFor(result).toLowerCase().startsWith(lowerQuery);

  // Sort results: exact matches first, then by relevance
  return results.sort((a, b) => {
    const aExact = isTopMatch(a);
    const bExact = isTopMatch(b);

    if (aExact && !bExact) return -1;
    if (!aExact && bExact) return 1;

    return textFor(a).localeCompare(textFor(b));
  });
};
